import React, { useMemo } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Box, Grid, Container, Stack } from '@mui/material'
import { motion } from 'framer-motion'
import { Icon } from '@iconify/react'
import linkedinIcon from '@iconify/icons-logos/linkedin-icon'
import githubIcon from '@iconify/icons-logos/github'
import twitterIcon from '@iconify/icons-logos/twitter'
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from "@vercel/speed-insights/react"
import './styles.css'
import { App } from './App'
import SlidingText from './slidingtext'
import Bottomslider from './bottomslider'
import About from './pages/about'
import LinksSection from './components/LinksSection'
import Skills from './components/Skills'
import Interests from './components/Interests'

const isNotMobile = window.screen.width > 700

function Root() {
  const iframeStyle = useMemo(() => ({
    border: "1px solid #777",
    maxWidth: isNotMobile ? "750px" : "85%",
    width: !isNotMobile ? "85%" : "100%",
    display: "block",
    margin: "0 auto",
  }), [])

  return (
    <div style={{ 
      position: 'relative', 
      display: 'flex', 
      flexDirection: 'column' 
    }}>
      {/* First section with reduced spacing */}
      <div style={{ minHeight: '80vh' }}>
        <SlidingText />
        <div className="appRender" 
          style={{ 
            width: "100vw", 
            height: "50vh", 
            minHeight: "400px", 
            marginBottom: "2vh" 
          }}>
          <Analytics/>
          <App />
          <SpeedInsights/>
        </div>
        <Bottomslider />
      </div>

      {/* Portfolio section */}
      <motion.div
        className="Portfolio"
        initial={{ x: '-100vw' }}
        animate={{ x: 0 }}
        transition={{ type: 'Spring', stiffness: 50, delay: 0.5, duration: 1 }}
        style={{ width: '100%', marginTop: '2vh' }}
      >
        <Box sx={{ flexGrow: 1, padding: isNotMobile ? '2rem' : '1rem' }}>
          <Grid container justifyContent="center">
            <Grid item xs={12}>
              <iframe
                src="https://indd.adobe.com/embed/c4eb285c-7b4f-49ee-bc38-5926bed0d74d?startpage=1&allowFullscreen=true"
                title="Portfolio"
                width="100%"
                height={isNotMobile ? "371px" : "300px"}
                style={iframeStyle}
                frameBorder="0"
                allowFullScreen
              />
            </Grid>
          </Grid>
        </Box>
      </motion.div>

      {/* Additional content section */}
      <div>
        <Box sx={{ flexGrow: 1, padding: isNotMobile ? '2rem' : '1rem' }}>
          <Grid container spacing={2} sx={{ minHeight: '1000px' }}>

            {/* Left column - LinksSection */}
            <Grid item xs={12} md={6} sx={{ height: '100%', display:'flex' }}>
              <Box sx={{width: '100%', height:'100%'}}>
              <LinksSection />
              </Box>
            </Grid>
            
            {/* Right column - About and Skills */}
            <Grid item xs={12} md={6} sx={{ height: '100%' }}>
              <Grid 
                container 
                direction="column" 
                spacing={2}
                sx={{ height: '100%' }}
              >
                <Grid item xs={12} sx={{ height: '33' }}>
                  <About
                    resumeBasicInfo={{
                      description_header: "Hi",
                      description: "Hi—I'm Alejandro Hernandez. I founded Galois Labs, where I'm building the AI-native platform for test & measurement engineering — a knowledge system that ingests LabVIEW binaries, manufacturer PDFs, and BOMs, and converts them into executable test sequences. Our AI agent controls real instruments with safety gates. Co-founded with Bhavik Sheoran (ex-Rivian). Platform built from zero. I also served as CTO at TORQ Sports, shipping 14 releases of a Flutter mobile app, a Node.js backend with AI features (Claude, Gemini, GPT via Bedrock), and a suite of internal tools from video highlight generation to engineering analytics. Previously, I contributed to EcoCar's autonomous vehicle team (sensor fusion, LiDAR), and founded Highlander Racing's autonomous racing platform. I studied Pure Mathematics and Computer Science, have nearly a decade of experience in software and IT, and am bilingual (Spanish). When not working, usually surfing or cycling at the beach.",
                      section_name: {
                        about: "About me"
                      }
                    }}
                    sharedBasicInfo={{
                      image: "null"
                    }}
                  />
                </Grid>
                <Grid item xs={12} sx={{ height: '33%' }}>
                <Skills skills={<>
                      <strong>Programming Languages:</strong> C/C++, Go, Python, Dart, Swift, R, MATLAB, Java, JavaScript/TypeScript, HTML5, LaTeX, Visual Basic, Wolfram Language, SQL, Embedded C
                      <br/><br/>
                      <strong>Mobile & Frontend:</strong> Flutter (14 production releases), React, Next.js, Material-UI, Framer Motion, SwiftUI
                      <br/><br/>
                      <strong>Backend & APIs:</strong> Node.js/Express, FastAPI, REST APIs, gRPC, GraphQL, WebSocket, SSE, Cobra CLI
                      <br/><br/>
                      <strong>AI/ML:</strong> PyTorch, TensorFlow, YOLO (segmentation), LTCN + Neural ODEs, LLM orchestration (Claude/Bedrock, Gemini, GPT), LangGraph, prompt engineering, Pandas
                      <br/><br/>
                      <strong>Cloud & DevOps:</strong> AWS (Bedrock, ECR, CloudWatch, EC2, S3), GCP, Firebase (Auth, FCM, Analytics), Azure, DigitalOcean, Docker, GitHub Actions, GitLab CI/CD, Tailscale, WiX MSI packaging
                      <br/><br/>
                      <strong>Embedded Systems:</strong> STM32 (F-series MCUs), TI AM263x, TI BQ796x ASICs, JTAG, SWD, ARM architectures, CUDA, FPGA (LX7), PCB Design (Altium), CAN/I2C/SPI/QSPI/UART protocols, RTOS, ESP32, NVIDIA Jetson
                      <br/><br/>
                      <strong>Protocols & Instrumentation:</strong> SCPI, VISA, Modbus, WebRTC, IEEE 488.2, National Instruments (hardware + software), Lock-in Amplifiers, Nanovoltmeters, Signal Generators, Oscilloscopes, Spectrum Analyzers
                      <br/><br/>
                      <strong>Autonomous Systems:</strong> AirSim, CARLA, SLAM, PnP, Sensor fusion, PX4, ROS, Renode, Verilator, RTMaps
                      <br/><br/>
                      <strong>Data & Enterprise:</strong> PostgreSQL, SQLite, asyncpg, ERPNext, Salesforce, Tableau, Selenium
                      <br/><br/>
                      <strong>Additional:</strong> Self-taught in derivatives trading and financial mathematical methods (dynamic hedging). International Data Engineering & Science Association member since 2017. Member of SHPE and SAE.
                    </>} />
                </Grid>
                <Grid item xs={12} sx={{height: '33%'}}>
                  <Interests interests={
                    <>
                    I build across the full stack—from bare-metal embedded systems to cloud platforms to mobile apps—with a throughline of making hardware and AI work together in production. At <strong>Galois Labs</strong>, that means building a knowledge platform for hardware engineering — three ingestion pipelines that turn LabVIEW files, PDFs, and BOMs into structured intelligence, an AI agent that executes on real instruments with safety gates, and a migration engine that parses NI's proprietary formats. At <strong>TORQ Sports</strong>, it meant shipping a full Flutter app through 14 releases, building a Node.js backend with LLM-powered features, and standing up internal tooling from video highlight generators to engineering analytics dashboards.
                  <br /> <br />
                  I'm drawn to problems that span multiple layers: LLM orchestration that controls physical hardware (LangGraph + real instruments), on-device ML for surgical environments (YOLO segmentation on iOS), real-time volumetric capture (multi-camera Gaussian splatting, CUDA/TensorRT, WebRTC streaming), and distributed architectures that bridge cloud and edge. My background in C++/CUDA GPU compute, autonomous vehicles (sensor fusion, LiDAR, PX4/ROS), and high-precision instrumentation (Barsukov Group) gave me a foundation in real-time systems, 3D reconstruction, and signal processing that continues to inform everything I build.
                  <br /> <br />
                  <strong>Currently</strong> focused on scaling Galois Labs and building Heimdall, an open-source volumetric video pipeline. Always interested in hard problems at the intersection of software, hardware, and AI.
                  </>
                  } />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* Social Links */}
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={4}
          sx={{ marginTop: 4, marginBottom: 4, padding: '2rem' }}
        >
          <a href="https://www.linkedin.com/in/hernan-tech" target="_blank" rel="noopener noreferrer">
            <Icon icon={linkedinIcon} width="32" height="32" />
          </a>
          <a href="https://github.com/hernantech" target="_blank" rel="noopener noreferrer">
            <Icon icon={githubIcon} width="32" height="32" />
          </a>
          <a href="https://twitter.com/cokeandmath" target="_blank" rel="noopener noreferrer">
            <Icon icon={twitterIcon} width="32" height="32" />
          </a>
        </Stack>
      </div>
    </div>
  )
}

createRoot(document.getElementById('root')).render(
  <BrowserRouter><Root /></BrowserRouter>
)